import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Bot, Globe, Zap, AlertTriangle, Check, X,
  FileText, Lock, Mic, MicOff, Video, Image, Send, Clock,
  CheckCircle2, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentConsentModal({ 
  isOpen, 
  onClose, 
  onConsent,
  companyName = "YASNO" 
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [hasVoiceConfirmation, setHasVoiceConfirmation] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        setAudioUrl(URL.createObjectURL(audioBlob));
        setHasVoiceConfirmation(true);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          stopRecording();
        }
      }, 10000);
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      toast.error("Could not access microphone. Voice confirmation is optional.");
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Clear recording
  const clearRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setHasVoiceConfirmation(false);
  };

  // Generate device fingerprint (simple version)
  const getDeviceFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    // Simple hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  };

  // Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleConsent = async () => {
    if (!isChecked) {
      toast.error("Please check the authorization box to continue");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare voice confirmation data
      let voiceAudioBase64 = null;
      if (audioBlob) {
        voiceAudioBase64 = await blobToBase64(audioBlob);
      }

      // Save agent consent with full audit trail
      const response = await axios.post(
        `${BACKEND_URL}/api/user/agent-consent`,
        {
          agent_authorization_accepted: true,
          consent_version: "v1.0 - Jan 2026",
          full_auto_mode_acknowledged: true,
          device_fingerprint: getDeviceFingerprint(),
          user_agent: navigator.userAgent,
          voice_confirmation_audio: voiceAudioBase64,
          voice_confirmation_transcript: hasVoiceConfirmation ? "I authorize this" : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local storage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.agent_consent_given = true;
      localStorage.setItem('user', JSON.stringify(user));

      // Show success with audit info
      const auditInfo = response.data.audit_trail;
      toast.success(
        <div>
          <p className="font-semibold">Agent authorization granted!</p>
          <p className="text-xs text-muted-foreground mt-1">
            Consent v{auditInfo.consent_version.split(' - ')[0]} • {new Date(auditInfo.timestamp).toLocaleString()}
          </p>
          {auditInfo.voice_confirmation_saved && (
            <p className="text-xs text-green-400 mt-1">✓ Voice confirmation saved</p>
          )}
        </div>
      );
      
      onConsent && onConsent();
      onClose();

    } catch (error) {
      console.error('Consent error:', error);
      toast.error("Failed to save consent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[85vh] overflow-hidden p-0 flex flex-col">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-white/10 bg-gradient-to-r from-primary/10 to-blue-600/10 flex-shrink-0">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Authorization & Consent Required
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Before enabling AI Agent features
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Scrollable Content */}
        <div 
          className="p-6 overflow-y-auto flex-1 min-h-0"
          onScroll={handleScroll}
        >
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20 mb-6">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-400">Important Notice</p>
              <p className="text-sm text-orange-400/80">
                Please read the following carefully. By proceeding, you grant significant autonomous powers to our AI system.
              </p>
            </div>
          </div>

          {/* What You're Authorizing */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wider">
              What You're Authorizing
            </h3>
            <div className="grid gap-3">
              {[
                {
                  icon: Bot,
                  title: "Digital Representative",
                  description: "AI Agents will act as your digital marketing representative"
                },
                {
                  icon: Globe,
                  title: "Social Media Management",
                  description: "Create and manage social media accounts on your behalf"
                },
                {
                  icon: Video,
                  title: "Content Generation",
                  description: "Generate multilingual videos, images, and written content"
                },
                {
                  icon: Send,
                  title: "Autonomous Publishing",
                  description: "Publish posts automatically based on your voice/text instructions"
                },
                {
                  icon: Zap,
                  title: "Full Auto Mode",
                  description: "Content may be published without manual human review"
                }
              ].map((item, i) => (
                <div 
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/5"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Your Responsibilities */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wider">
              Your Responsibilities
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>You accept full responsibility for the instructions you provide to the AI</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>You ensure your instructions comply with platform guidelines and laws</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>You acknowledge content may be published without your review in Full Auto Mode</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>You can revoke this authorization at any time from Settings</span>
              </li>
            </ul>
          </div>

          {/* Voice Confirmation (Optional but Recommended) */}
          <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-blue-400">
                Voice Confirmation (Recommended)
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              For enhanced legal protection, record yourself saying "I authorize this" to create an audio audit trail.
            </p>
            
            {!hasVoiceConfirmation ? (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={isRecording ? stopRecording : startRecording}
                >
                  {isRecording ? (
                    <>
                      <MicOff className="w-4 h-4 mr-2" />
                      Stop Recording
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Record "I authorize this"
                    </>
                  )}
                </Button>
                {isRecording && (
                  <div className="flex items-center gap-2 text-red-400 text-sm animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-red-400"></span>
                    Recording...
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/20 border border-green-500/30">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Voice recorded</span>
                </div>
                <audio src={audioUrl} controls className="h-8" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearRecording}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Legal Links */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
            <a href="/terms" target="_blank" className="flex items-center gap-1 text-primary hover:underline">
              <FileText className="w-4 h-4" />
              Terms of Service
            </a>
            <a href="/privacy" target="_blank" className="flex items-center gap-1 text-primary hover:underline">
              <Lock className="w-4 h-4" />
              Privacy Policy
            </a>
          </div>
        </div>

        {/* Consent Checkbox & Actions */}
        <div className="p-6 pt-4 border-t border-white/10 bg-white/5 flex-shrink-0">
          {/* Audit Trail Info */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Consent v1.0 - Jan 2026</span>
            </div>
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span>Full audit trail will be saved</span>
            </div>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20 mb-4">
            <Checkbox
              id="agent-consent"
              checked={isChecked}
              onCheckedChange={setIsChecked}
              className="mt-1"
              data-testid="agent-consent-checkbox"
            />
            <label 
              htmlFor="agent-consent" 
              className="text-sm leading-relaxed cursor-pointer"
            >
              <span className="font-semibold text-primary">AUTHORIZATION & CONSENT</span>
              <br />
              I hereby authorize <span className="font-semibold">{companyName}</span> and its AI Agents to act as my digital representative. 
              I grant them the power to create and manage social media accounts, generate multilingual content, 
              and publish posts autonomously on my behalf based on my voice/text instructions. 
              I accept full responsibility for the instructions provided and acknowledge that 
              <span className="text-orange-400 font-medium"> "Full Auto Mode" involves publishing content without manual human review</span>. 
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" className="text-primary hover:underline">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</a>.
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={onClose}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              className={`flex-1 rounded-xl transition-all ${
                isChecked 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 neon-glow' 
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
              onClick={handleConsent}
              disabled={!isChecked || isSubmitting}
              data-testid="agent-consent-submit"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving consent...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  I Authorize & Accept
                </div>
              )}
            </Button>
          </div>

          {/* Hint */}
          {!isChecked && (
            <p className="text-xs text-center text-muted-foreground mt-3">
              Please check the authorization box above to continue
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
